/**
 * Annotation utilities for converting between ChessBase and Chessground shape formats
 *
 * ChessBase format (used in PGN):
 * - Modern format:
 *   - [%csl ColorSquare,ColorSquare,...] for colored squares (circles)
 *   - [%cal ColorFromSquareToSquare,ColorFromSquareToSquare,...] for colored arrows
 *   - Colors: R=red, G=green, Y=yellow, B=blue
 * - Legacy format (older ChessBase):
 *   - [%draw arrow,fromSquare,toSquare,colorName] for arrows
 *   - [%draw full,square,colorName] for colored squares (circles)
 *   - Colors: full color names (red, green, yellow, blue)
 *
 * Chessground format (used in UI):
 * - { orig: 'e4', brush: 'green' } for circles
 * - { orig: 'e2', dest: 'e4', brush: 'blue' } for arrows
 */
/**
 * Color mapping between ChessBase and Chessground
 */
const COLOR_MAP = {
    R: "red",
    G: "green",
    Y: "yellow",
    B: "blue",
};
const REVERSE_COLOR_MAP = {
    red: "R",
    green: "G",
    yellow: "Y",
    blue: "B",
};
/**
 * Color name mapping for legacy draw format (full color names)
 */
const COLOR_NAME_MAP = {
    red: "red",
    green: "green",
    yellow: "yellow",
    blue: "blue",
};
/**
 * Parse ChessBase square annotation (e.g., "Re5" -> { orig: 'e5', brush: 'red' })
 */
function parseChessBaseSquare(annotation) {
    if (annotation.length < 2)
        return null;
    const colorCode = annotation[0].toUpperCase();
    const square = annotation.slice(1).toLowerCase();
    if (!COLOR_MAP[colorCode])
        return null;
    if (!/^[a-h][1-8]$/.test(square))
        return null;
    return {
        orig: square,
        brush: COLOR_MAP[colorCode],
    };
}
/**
 * Parse ChessBase arrow annotation (e.g., "Gf3e5" -> { orig: 'f3', dest: 'e5', brush: 'green' })
 */
function parseChessBaseArrow(annotation) {
    if (annotation.length < 5)
        return null;
    const colorCode = annotation[0].toUpperCase();
    const fromSquare = annotation.slice(1, 3).toLowerCase();
    const toSquare = annotation.slice(3, 5).toLowerCase();
    if (!COLOR_MAP[colorCode])
        return null;
    if (!/^[a-h][1-8]$/.test(fromSquare))
        return null;
    if (!/^[a-h][1-8]$/.test(toSquare))
        return null;
    return {
        orig: fromSquare,
        dest: toSquare,
        brush: COLOR_MAP[colorCode],
    };
}
/**
 * Parse legacy draw arrow annotation
 * (e.g., "arrow,a4,c2,green" -> { orig: 'a4', dest: 'c2', brush: 'green' })
 */
function parseLegacyDrawArrow(content) {
    const parts = content.split(",").map((s) => s.trim());
    // Expected format: arrow,fromSquare,toSquare,colorName
    if (parts.length !== 4 || parts[0] !== "arrow")
        return null;
    const fromSquare = parts[1].toLowerCase();
    const toSquare = parts[2].toLowerCase();
    const colorName = parts[3].toLowerCase();
    if (!/^[a-h][1-8]$/.test(fromSquare))
        return null;
    if (!/^[a-h][1-8]$/.test(toSquare))
        return null;
    if (!COLOR_NAME_MAP[colorName])
        return null;
    return {
        orig: fromSquare,
        dest: toSquare,
        brush: COLOR_NAME_MAP[colorName],
    };
}
/**
 * Parse legacy draw circle annotation
 * (e.g., "full,e4,green" -> { orig: 'e4', brush: 'green' })
 */
function parseLegacyDrawCircle(content) {
    const parts = content.split(",").map((s) => s.trim());
    // Expected format: full,square,colorName
    if (parts.length !== 3 || parts[0] !== "full")
        return null;
    const square = parts[1].toLowerCase();
    const colorName = parts[2].toLowerCase();
    if (!/^[a-h][1-8]$/.test(square))
        return null;
    if (!COLOR_NAME_MAP[colorName])
        return null;
    return {
        orig: square,
        brush: COLOR_NAME_MAP[colorName],
    };
}
/**
 * Convert ChessBase annotation string to Chessground shapes
 * @param chessBaseAnnotation - String like "[%csl Re5,Yc6,Gb1]" or "[%cal Gf3e5,Rb5c6]"
 *                              or legacy "[%draw arrow,a4,c2,green]" or "[%draw full,e4,red]"
 * @returns Array of Chessground shapes
 */
export function chessBaseToChessground(chessBaseAnnotation) {
    const shapes = [];
    // Parse [%csl ...] annotations (colored squares)
    const cslMatch = chessBaseAnnotation.match(/\[%csl\s+([^\]]+)\]/);
    if (cslMatch) {
        const items = cslMatch[1].split(",").map((s) => s.trim());
        for (const item of items) {
            const shape = parseChessBaseSquare(item);
            if (shape)
                shapes.push(shape);
        }
    }
    // Parse [%cal ...] annotations (colored arrows)
    const calMatch = chessBaseAnnotation.match(/\[%cal\s+([^\]]+)\]/);
    if (calMatch) {
        const items = calMatch[1].split(",").map((s) => s.trim());
        for (const item of items) {
            const shape = parseChessBaseArrow(item);
            if (shape)
                shapes.push(shape);
        }
    }
    // Parse [%draw ...] annotations (legacy format for both arrows and circles)
    const drawMatches = chessBaseAnnotation.matchAll(/\[%draw\s+([^\]]+)\]/g);
    for (const match of drawMatches) {
        // Try parsing as arrow first, then as circle
        const arrowShape = parseLegacyDrawArrow(match[1]);
        if (arrowShape) {
            shapes.push(arrowShape);
        }
        else {
            const circleShape = parseLegacyDrawCircle(match[1]);
            if (circleShape)
                shapes.push(circleShape);
        }
    }
    return shapes;
}
/**
 * Convert Chessground shapes to ChessBase annotation string
 * @param shapes - Array of Chessground shapes
 * @returns ChessBase annotation string like "[%csl Re5,Yc6][%cal Gf3e5]"
 */
export function chessgroundToChessBase(shapes) {
    if (!shapes || shapes.length === 0)
        return "";
    const squares = [];
    const arrows = [];
    for (const shape of shapes) {
        const colorCode = REVERSE_COLOR_MAP[shape.brush];
        if (!colorCode)
            continue;
        if (shape.dest) {
            // Arrow
            arrows.push(`${colorCode}${shape.orig}${shape.dest}`);
        }
        else {
            // Square (circle)
            squares.push(`${colorCode}${shape.orig}`);
        }
    }
    let result = "";
    if (squares.length > 0) {
        result += `[%csl ${squares.join(",")}]`;
    }
    if (arrows.length > 0) {
        result += `[%cal ${arrows.join(",")}]`;
    }
    return result;
}
/**
 * Check if a comment contains ChessBase annotations
 */
export function hasChessBaseAnnotations(comment) {
    return /\[%(?:csl|cal|draw)\s+[^\]]+\]/.test(comment);
}
/**
 * Extract puzzle annotation from a comment, or null if no puzzle marker is present.
 * All puzzle fields (mateIn, aiLevel, attempts, timeControl) come from the same {[#...]} block.
 */
export function extractPuzzleAnnotationFromComment(comment) {
    if (!/\[#[^\]]*\]/.test(comment))
        return null;
    const annotation = {
        mateIn: extractMateInFromComment(comment) ?? 1,
        aiLevel: extractAiLevelFromComment(comment) ?? 0,
        attempts: extractAttemptsFromComment(comment),
    };
    const timeControl = extractTimeControlFromComment(comment);
    if (timeControl != null)
        annotation.timeControl = timeControl;
    return annotation;
}
/**
 * Extract ChessBase annotations from a comment and return both the annotations and cleaned comment
 * @param comment - Comment that may contain annotations (with or without curly braces)
 * @returns Object with shapes, mcq, puzzleAnnotation, orient, and cleaned comment (without annotation syntax)
 */
export function extractAnnotationsFromComment(comment) {
    // Remove curly braces if present - allows function to work with both "{[#]}" and "[#]"
    const normalizedComment = comment.replace(/^\{|\}$/g, "");
    const shapes = chessBaseToChessground(normalizedComment);
    const mcq = chessBaseToMcq(normalizedComment);
    const puzzleAnnotation = extractPuzzleAnnotationFromComment(normalizedComment);
    const orient = extractOrientFromComment(normalizedComment);
    // Remove annotation syntax from comment (both modern and legacy formats)
    // Also filter out %evp annotations (engine evaluations) which are not needed in the UI
    const cleanComment = normalizedComment
        .replace(/\[%csl\s+[^\]]+\]/g, "")
        .replace(/\[%cal\s+[^\]]+\]/g, "")
        .replace(/\[%draw\s+[^\]]+\]/g, "")
        .replace(/\[%evp\s*[^\]]+\]/g, "")
        .replace(/\[%mcq\s+[^\]]+\]/g, "")
        .replace(/\[%orient\s+[wb]\]/g, "")
        .replace(/\[#[^\]]*\]/g, "") // Remove all [#...] puzzle marker variants
        .replace(/^\{\s*\}\s*$/g, "") // Remove empty curly braces
        .trim();
    return { shapes, mcq, puzzleAnnotation, orient, cleanComment };
}
/**
 * Validate that a shape has valid square coordinates
 */
export function isValidShape(shape) {
    const isValidSquare = (sq) => /^[a-h][1-8]$/.test(sq);
    if (!isValidSquare(shape.orig))
        return false;
    if (shape.dest && !isValidSquare(shape.dest))
        return false;
    return ["red", "green", "yellow", "blue"].includes(shape.brush);
}
/**
 * Filter out invalid shapes from an array
 */
export function filterValidShapes(shapes) {
    return shapes.filter(isValidShape);
}
/**
 * Convert MCQ to ChessBase annotation format
 * Format: [%mcq question="What is the best move?" options="Nf3|e4|d4" correct="1"]
 * @param mcq - MCQ annotation object
 * @returns ChessBase annotation string
 */
export function mcqToChessBase(mcq) {
    if (!mcq || !mcq.question || !mcq.options || mcq.options.length < 2) {
        return "";
    }
    // Escape quotes in question and options
    const escapeQuotes = (str) => str.replace(/"/g, '\\"');
    const question = escapeQuotes(mcq.question);
    const options = mcq.options.map(escapeQuotes).join("|");
    const correct = mcq.correctAnswer;
    const mcqClockPart = mcq.mcqClock != null ? ` mcq_clock="${mcq.mcqClock}"` : "";
    return `[%mcq question="${question}" options="${options}" correct="${correct}"${mcqClockPart}]`;
}
/**
 * Parse MCQ from ChessBase annotation format
 * Format: [%mcq question="What is the best move?" options="Nf3|e4|d4" correct="1"]
 * @param annotation - ChessBase annotation string
 * @returns MCQ annotation object or null if invalid
 */
export function chessBaseToMcq(annotation) {
    const mcqMatch = annotation.match(/\[%mcq\s+([^\]]+)\]/);
    if (!mcqMatch)
        return null;
    const content = mcqMatch[1];
    // Extract question
    const questionMatch = content.match(/question="([^"\\]*(?:\\.[^"\\]*)*)"/);
    if (!questionMatch)
        return null;
    const question = questionMatch[1].replace(/\\"/g, '"');
    // Extract options
    const optionsMatch = content.match(/options="([^"\\]*(?:\\.[^"\\]*)*)"/);
    if (!optionsMatch)
        return null;
    const optionsStr = optionsMatch[1].replace(/\\"/g, '"');
    const options = optionsStr.split("|").map((opt) => opt.trim());
    if (options.length < 2)
        return null;
    // Extract correct answer
    const correctMatch = content.match(/correct="(\d+)"/);
    if (!correctMatch)
        return null;
    const correctAnswer = parseInt(correctMatch[1], 10);
    if (correctAnswer < 0 || correctAnswer >= options.length)
        return null;
    // Extract optional mcq_clock
    const mcqClockMatch = content.match(/mcq_clock="(\d+)"/);
    const mcqClock = mcqClockMatch ? parseInt(mcqClockMatch[1], 10) : undefined;
    return {
        question,
        options,
        correctAnswer,
        ...(mcqClock != null && mcqClock > 0 ? { mcqClock } : {}),
    };
}
/**
 * Check if a comment contains MCQ annotation
 */
export function hasMcqAnnotation(comment) {
    return /\[%mcq\s+[^\]]+\]/.test(comment);
}
/**
 * Extract MCQ annotation from a comment and return both the MCQ and cleaned comment
 * @param comment - Comment that may contain MCQ annotation
 * @returns Object with mcq and cleaned comment (without MCQ annotation syntax)
 */
export function extractMcqFromComment(comment) {
    const mcq = chessBaseToMcq(comment);
    // Remove MCQ annotation syntax from comment
    const cleanComment = comment.replace(/\[%mcq\s+[^\]]+\]/g, "").trim();
    return { mcq, cleanComment };
}
/**
 * Extract attempts parameter from puzzle marker annotation
 * Format: {[# attempts="2"]} or {[# attempts=2]}
 * @param comment - Comment that may contain attempts annotation
 * @returns Number of attempts allowed, or null if not specified
 */
export function extractAttemptsFromComment(comment) {
    // Match [# attempts="2"] or [# attempts=2] (with or without quotes)
    const attemptsMatch = comment.match(/\[#[^\]]*attempts="?(\d+)"?[^\]]*\]/);
    if (!attemptsMatch)
        return null;
    const attempts = parseInt(attemptsMatch[1], 10);
    return attempts > 0 ? attempts : null;
}
/**
 * Extract mate_in parameter from puzzle marker annotation
 * Format: {[# mate_in="2"]} or {[# mate_in=2]}
 * @param comment - Comment that may contain mate_in annotation
 * @returns Number of moves to achieve checkmate, or null if not specified
 */
export function extractMateInFromComment(comment) {
    const match = comment.match(/\[#[^\]]*mate_in="?(\d+)"?[^\]]*\]/);
    if (!match)
        return null;
    const value = parseInt(match[1], 10);
    return value > 0 ? value : null;
}
/**
 * Extract ai_level parameter from puzzle marker annotation
 * Format: {[# ai_level="4"]} or {[# ai_level=4]}
 * @param comment - Comment that may contain ai_level annotation
 * @returns AI difficulty level (0 = random), or null if not specified
 */
export function extractAiLevelFromComment(comment) {
    const match = comment.match(/\[#[^\]]*ai_level="?(\d+)"?[^\]]*\]/);
    if (!match)
        return null;
    const value = parseInt(match[1], 10);
    return value >= 0 ? value : null;
}
/**
 * Extract time control from puzzle marker annotation.
 * Parses puzzle_clock, attempt_clock, move_clock from {[#...]} comments.
 * @param comment - Comment that may contain clock annotations
 * @returns TimeControl object, or null if no clock is specified
 */
export function extractTimeControlFromComment(comment) {
    const extractClock = (key) => {
        const match = comment.match(new RegExp(`\\[#[^\\]]*${key}="?(\\d+)"?[^\\]]*\\]`));
        if (!match)
            return null;
        const value = parseInt(match[1], 10);
        return value > 0 ? value : null;
    };
    const puzzleClock = extractClock("puzzle_clock");
    const attemptClock = extractClock("attempt_clock");
    const moveClock = extractClock("move_clock");
    if (puzzleClock == null && attemptClock == null && moveClock == null) {
        return null;
    }
    const tc = {};
    if (puzzleClock != null)
        tc.puzzleClock = puzzleClock;
    if (attemptClock != null)
        tc.attemptClock = attemptClock;
    if (moveClock != null)
        tc.moveClock = moveClock;
    return tc;
}
/**
 * Extract board orientation from annotation
 * Format: [%orient w] or [%orient b]
 * @param comment - Comment that may contain orient annotation
 * @returns "w" or "b" for the side to orient the board for, or null if not specified
 */
export function extractOrientFromComment(comment) {
    const orientMatch = comment.match(/\[%orient\s+([wb])\]/);
    if (!orientMatch)
        return null;
    return orientMatch[1];
}
/**
 * Convert board orientation to ChessBase annotation format
 * @param orient - "w" or "b"
 * @returns ChessBase annotation string e.g. "[%orient w]"
 */
export function orientToChessBase(orient) {
    return `[%orient ${orient}]`;
}
